// src/components/DevToolsAuto.tsx

'use client'

import React, { useEffect, useState } from 'react'
import { DevToolsTrigger } from './DevToolsLauncher'
import { DevTools as DevToolsCore } from './DevTools'
import { DevToolsProps } from '../types'

interface DevToolsAutoProps {
  url?: string
  hideInProduction?: boolean
  children?: React.ReactNode
}

// Automatická detekce development environment (browser-only)
function isDevelopment(): boolean {
  // Check pro browser environment
  if (typeof window === 'undefined') {
    return false // SSR safe
  }

  // Check NODE_ENV pokud je dostupné
  if (typeof globalThis !== 'undefined') {
    const globalProcess = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process
    if (globalProcess?.env?.NODE_ENV) {
      return globalProcess.env.NODE_ENV === 'development'
    }
  }

  // Fallback checks pro browser
  const port = window.location.port
  const devPorts = ['3000', '3001', '5173', '8080', '4200', '8000', '9000']

  // Check hostname
  const hostname = window.location.hostname
  const devHosts = ['localhost', '127.0.0.1', '0.0.0.0']

  // Check for dev indicators
  const isDevHost = devHosts.includes(hostname)
  const isDevPort = devPorts.includes(port)
  const hasDevQuery = window.location.search.includes('dev=true')

  return isDevHost || isDevPort || hasDevQuery
}

/**
 * DevTools komponenta s automatickou detekcí development environment
 * Použití: <DevTools /> - automaticky se skryje v produkci
 */
export function DevTools(props: DevToolsAutoProps = {}) {
  const { hideInProduction = true, children } = props
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    const isDevEnv = isDevelopment()
    const shouldHide = hideInProduction && !isDevEnv
    const hasNoDevToolsParam =
      new URLSearchParams(window.location.search).get('no-dev-tools') === 'true'

    setShouldShow(!shouldHide && !hasNoDevToolsParam)
  }, [hideInProduction])

  // Pokud má children, použij jako wrapper
  if (children) {
    return (
      <>
        {children}
        {shouldShow && <DevToolsTrigger />}
      </>
    )
  }

  // Pokud nemá children, použij jen trigger
  if (!shouldShow) {
    return null
  }

  return <DevToolsTrigger />
}

/**
 * DevTools launcher komponenta - vždy viditelná wrapper
 * Použití: <DevToolsLauncher>{children}</DevToolsLauncher>
 */
export function DevToolsLauncher({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <DevTools />
    </>
  )
}

/**
 * Manuální DevTools komponenta - pro pokročilé použití
 * Použití: const {isOpen, toggle} = useDevTools(); <DevToolsManual isOpen={isOpen} onToggle={toggle} />
 */
export function DevToolsManual(props: DevToolsProps) {
  const [shouldShow, setShouldShow] = useState(false)

  useEffect(() => {
    const hasNoDevToolsParam =
      new URLSearchParams(window.location.search).get('no-dev-tools') === 'true'
    setShouldShow(!hasNoDevToolsParam)
  }, [])

  if (!shouldShow) {
    return null
  }

  return <DevToolsCore {...props} />
}

/**
 * Force DevTools - vždy viditelná bez kontrol
 * Použití: <DevToolsForce /> - pro testování
 */
export function DevToolsForce(props: Omit<DevToolsAutoProps, 'hideInProduction'>) {
  return <DevTools {...props} hideInProduction={false} />
}
